import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/mocks/news";
import { formatDate, getCategoryName } from "@/utils/news";

export default function HeroCarousel({ slides }: { slides: Article[] }) {
  const [index, setIndex] = useState(0);
  const total = slides.length;

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5000);
    return () => clearInterval(timer);
  }, [total]);

  const go = (i: number) => setIndex((i + total) % total);
  const active = slides[index];

  if (!active) return null;

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.image}
            alt={s.title}
            title={`${s.title} - 51爆料`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/25" />

      <div className="absolute inset-0 w-full px-4 md:px-6 flex items-end pb-14 md:pb-16">
        <div className="max-w-7xl mx-auto w-full">
          <div key={active.id} className="max-w-2xl animate-fade-up">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-xs px-2.5 py-1 rounded-full bg-primary-500 text-white whitespace-nowrap">
                {getCategoryName(active.category)}
              </span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-white/15 text-white backdrop-blur whitespace-nowrap">
                {active.subCategory}
              </span>
              <span className="text-xs text-white/70 whitespace-nowrap">
                {formatDate(active.date)}
              </span>
            </div>
            <h2 className="text-2xl md:text-4xl font-heading font-black text-white leading-tight">
              <Link
                to={`/detail/${active.id}`}
                className="hover:text-primary-200 transition-colors cursor-pointer"
              >
                {active.title}
              </Link>
            </h2>
            <p className="mt-3 text-sm md:text-base text-white/80 line-clamp-2 hidden sm:block">
              {active.summary}
            </p>
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-1 mt-5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              查看爆料详情
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 text-white backdrop-blur transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 text-white backdrop-blur transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 md:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`第${i + 1}张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-primary-500" : "w-3 bg-white/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}